import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useSession } from '../../lib/store';
import { Card, Empty, Notice, PageHeading, Row, Spinner } from '../../components/Shell';
import { InvoiceStamp } from '../../components/Stamp';
import { SelectField } from '../../components/Field';
import { money } from '../../lib/format';
import { dateRange, longDate } from '../../lib/format';
import type { Booking, ClientProfile, Invoice } from '../../lib/types';

/**
 * Staff-side invoicing: turn a booking's already-priced quote into a draft
 * invoice, send it, or void it. There is nowhere here to type in a price —
 * every invoice is a copy of the booking's quote, which is what keeps the
 * two from ever disagreeing about what is owed.
 */
export function AdminInvoices() {
  const session = useSession();
  const tenantId = session.tenant?.tenantId;

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [clients, setClients] = useState<ClientProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [creating, setCreating] = useState(false);
  const [busyInvoiceId, setBusyInvoiceId] = useState<string | null>(null);

  const refreshInvoices = () => {
    if (!tenantId) return Promise.resolve();
    return api<{ invoices: Invoice[] }>(`/tenants/${tenantId}/invoices`)
      .then((result) => {
        setInvoices(result.invoices);
        setError(null);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Invoices could not be loaded.'));
  };

  useEffect(() => {
    if (!tenantId) return;
    void Promise.all([
      refreshInvoices(),
      api<{ bookings: Booking[] }>(`/tenants/${tenantId}/bookings`).then((r) => setBookings(r.bookings)),
      api<{ clients: ClientProfile[] }>(`/tenants/${tenantId}/clients`).then((r) => setClients(r.clients)),
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const clientName = (clientId: string) => clients.find((c) => c.clientId === clientId)?.fullName ?? 'A client';

  const bookingLabel = (booking: Booking) =>
    `${clientName(booking.clientId)}, ${dateRange(booking.startDate, booking.endDate)} (${money(booking.quote.total)})`;

  const invoiceableBookings = bookings.filter(
    (b) => !['cancelled', 'declined'].includes(b.status)
  );

  const createInvoice = (event: React.FormEvent) => {
    event.preventDefault();
    if (!tenantId || !selectedBookingId) return;

    setCreating(true);
    setError(null);
    void api(`/tenants/${tenantId}/bookings/${selectedBookingId}/invoices`, { method: 'POST' })
      .then(() => {
        setSelectedBookingId('');
        return refreshInvoices();
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'That invoice could not be created.'))
      .finally(() => setCreating(false));
  };

  const sendInvoice = (invoiceId: string) => {
    if (!tenantId) return;
    setBusyInvoiceId(invoiceId);
    void api(`/tenants/${tenantId}/invoices/${invoiceId}/send`, { method: 'POST' })
      .then(refreshInvoices)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'That invoice could not be sent.'))
      .finally(() => setBusyInvoiceId(null));
  };

  const voidInvoice = (invoiceId: string) => {
    if (!tenantId) return;
    if (!window.confirm('Void this invoice? This cannot be undone.')) return;
    setBusyInvoiceId(invoiceId);
    void api(`/tenants/${tenantId}/invoices/${invoiceId}/void`, { method: 'POST' })
      .then(refreshInvoices)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'That invoice could not be voided.'))
      .finally(() => setBusyInvoiceId(null));
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-5 py-16">
        <Spinner label="Loading invoices" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-5 pb-24 pt-12">
      <PageHeading
        eyebrow={session.tenant?.businessName}
        title="Invoices"
        lead="Every invoice is a copy of a booking's quote at the moment it was issued. To bill a different amount, adjust the booking first."
      />

      {error && (
        <div className="mb-8">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      <Card className="p-6">
        <form onSubmit={createInvoice} className="flex flex-wrap items-end gap-4">
          <div className="min-w-0 flex-1">
            <SelectField
              label="Create an invoice from a booking"
              value={selectedBookingId}
              onChange={setSelectedBookingId}
              options={[
                { value: '', label: 'Choose a booking' },
                ...invoiceableBookings.map((b) => ({ value: b.bookingId, label: bookingLabel(b) })),
              ]}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={creating || !selectedBookingId}>
            {creating ? 'Creating' : 'Create invoice'}
          </button>
        </form>
      </Card>

      <div className="mt-8">
        {invoices.length === 0 ? (
          <Empty title="No invoices yet" body="Create one from a booking above." />
        ) : (
          <ul className="space-y-3">
            {invoices.map((invoice) => (
              <li key={invoice.invoiceId} className="card p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="min-w-0 truncate font-display text-lg font-bold">{invoice.number}</p>
                    <p className="typed text-xs text-[color:var(--color-ink-soft)]">
                      {clientName(invoice.clientId)} &middot; issued {longDate(invoice.createdAt)}
                      {invoice.sentTo && ` · sent to ${invoice.sentTo}`}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <InvoiceStamp status={invoice.status} flat />
                    <span className="font-display text-lg font-extrabold">{money(invoice.totalCents)}</span>
                  </div>
                </div>

                {invoice.dueDate && (
                  <div className="mt-3">
                    <Row label="Due">{invoice.dueDate}</Row>
                  </div>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  {invoice.status === 'draft' && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => sendInvoice(invoice.invoiceId)}
                      disabled={busyInvoiceId === invoice.invoiceId}
                    >
                      {busyInvoiceId === invoice.invoiceId ? 'Sending' : 'Send to client'}
                    </button>
                  )}
                  {(invoice.status === 'draft' || invoice.status === 'sent') && (
                    <button
                      type="button"
                      className="btn btn-quiet"
                      onClick={() => voidInvoice(invoice.invoiceId)}
                      disabled={busyInvoiceId === invoice.invoiceId}
                    >
                      Void
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
